import {Typography, TypographyProps} from "@mui/material";
import {memo} from "react";
import {IFRAME_TEMPLATES} from "constants/common";

interface TextProps extends TypographyProps {
  text: string
}

const Text = memo(({text, ...props}: TextProps) => {
  const createIframe = (content: string) => {
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = content;

    const oembedElements = tempContainer.querySelectorAll("oembed[url]");

    oembedElements.forEach((oembed) => {
      const url = oembed.getAttribute("url");
      const figure = oembed.closest("figure");

      if (figure && url) {
        for (const template of IFRAME_TEMPLATES) {
          const match = url.match(template.url as RegExp);
          if (match) {
            const iframeHtml = template.html(match);

            figure.className = `embed media ck-widget${template.name === 'yandexMaps' || template.name === 'yandexForms' ? ' full-width' : ''}`;
            figure.setAttribute("contenteditable", "false");

            figure.innerHTML = `
            <div class="ck-media__wrapper" data-oembed-url="${url}">
              ${iframeHtml}
            </div>
          `;
            break;
          }
        }
      }
    });

    return tempContainer.innerHTML;
  };

  return (
    <Typography {...props}
                className={'ck-content'}
                sx={(theme) => ({
                  '& .full-width iframe': {
                    width: '100%'
                  },
                  [theme.breakpoints.down('md')]: {
                    '& .embed:not(.full-width) iframe': {
                      width: '100%',
                      aspectRatio: 16/9
                    },
                  },
                  '& a': {wordBreak: 'break-all', textDecoration: 'underline'},
                  '& .image': {
                    '& > figcaption': {
                      display: 'table-caption',
                      captionSide: 'bottom',
                      wordBreak: 'break-word',
                      outlineOffset: -1,
                      backgroundColor: 'transparent'
                    },
                  },
                  '& ul, & ol': {
                    margin: 0,
                    marginLeft: 2,
                    padding: 0,
                    '& > li': {
                      mb: 2,
                      '& span': {
                        ...theme.typography.p2
                      }
                    },
                  },
                  '& .table': {
                    borderRadius: 5,
                    overflowX: 'auto',
                    border: `1px solid ${theme.palette.grey[100]}`,
                    display: 'block',
                    '& > table': {
                      borderCollapse: 'collapse',
                      width: '100%',

                    },
                    '& > table th': {
                      background: theme.palette.grey[400],
                      border: `1px double ${theme.palette.grey[100]}`,
                      minWidth: 'max-content',
                      px: 2,
                      py: 1,
                      '& > p': {
                        ...theme.typography.h6
                      }

                    },
                    '&  > table td': {
                      backgroundColor: '#fff',
                      border: `1px double ${theme.palette.grey[100]}`,
                      minWidth: 'max-content',
                      px: 2,
                      py: 1,
                      '& > p': {
                        ...theme.typography.p2
                      }

                    },
                    '& table tr:last-of-type td:first-of-type, & table tbody tr:last-of-type th:first-child': {
                      borderBottomLeftRadius: '20px',
                    },
                    '& table tr:first-of-type th:last-child': {
                      borderTopRightRadius: '20px',
                    },
                    '& table thead tr:first-of-type th:first-child': {
                      borderTopLeftRadius: '20px',
                    },
                    '& table tbody tr:last-child td:last-child': {
                      borderBottomRightRadius: '20px',
                    },
                    '& table tr:last-child td:last-child': {
                      borderBottomRightRadius: '20px',
                    },
                  },
                  [theme.breakpoints.up('xxs')]: {
                    '& h1': {
                      ...theme.typography.h1,
                      fontSize: 28,
                      m: 0,
                      textTransform: 'uppercase',
                      mb: 4
                    },
                    '& h2': {
                      ...theme.typography.h2
                    },
                    '& h3': {
                      ...theme.typography.h3
                    },
                    '& h4': {
                      ...theme.typography.h4
                    },
                    '& h5': {
                      ...theme.typography.h5
                    },
                    '& h6': {
                      ...theme.typography.h6
                    },
                    '& p': {
                      ...theme.typography.p2,
                      m: 0
                    },
                    '& blockquote': {
                      borderColor: 'grey.200',
                      pl: 2,
                      pr: 0,
                      '& > p': {
                        ...theme.typography.p2,
                        fontStyle: 'italic',
                      },
                    },

                    '& img': {
                      width: '100%',
                      height: 'auto'
                    },
                    '& figure': {
                      m: 0
                    },
                    '& figcaption': {
                      ...theme.typography.caption1
                    }
                  },
                  [theme.breakpoints.up('md')]: {
                    '& h1': {
                      fontSize: 48,
                    },
                  },
                  [theme.breakpoints.up('xl')]: {
                    '& h1': {
                      fontSize: 60,
                      mb: 5
                    },
                  }
                })}
                dangerouslySetInnerHTML={{
                  __html: createIframe(text).replace(/(padding|border(-\w+)?(-style|-color)?|order):\s*[^;]+;?/g, ''),
                }}/>
  )
})

export default Text;
